import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Spin, Space, Modal, message, Popconfirm } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery, gql, useLazyQuery, useMutation } from '@apollo/client';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';

import DataGrid from '../../components/DataGrid';
import * as Config from '../../appConfig';
import { activePeriod, disabledDate } from '../../utils/date';

import {
  updateTableMetaData,
  updateGridMetaData,
  resetGridFilters,
} from '../../store/reducers/expenseSlice';
import { ALLOWANCE_LIST_FIELDS } from '../../apollo/fragments';
import {
  ADD_ALLOWANCE_MUTATION,
  UPDATE_ALLOWANCE_MUTATION,
  DELETE_ALLOWANCE_MUTATION,
} from '../../apollo/mutations';
import {
  EMPLOYEE_LOOKUP_QUERY,
  ALLOWANCE_TYPE_OPTIONS_QUERY,
} from '../../apollo/queries';

import AddAllowanceForm from '../../components/Expense/Forms/AddAllowanceForm';
import ProfileCard from '../../components/Employee/ProfileCard';

const ALLOWANCES_QUERY = gql`
  ${ALLOWANCE_LIST_FIELDS}
  query Allowances(
    $first: Int
    $page: Int
    $filters: [QueryGetAllowancesFilter]
    $sort: QueryGetAllowancesSorter
  ) {
    allowances(first: $first, page: $page, filters: $filters, sort: $sort) {
      paginatorInfo {
        count
        currentPage
        total
      }
      data {
        ...AllowanceListFields
      }
    }
  }
`;

const Allowances = ({ gridId }) => {
  const { metaData, grids } = useSelector((state) => state.expense);
  const {
    user: { ability },
  } = useSelector((state) => state.login);
  const dispatch = useDispatch();

  const grid = grids.find((i) => i.id === gridId);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showAddEditAllowanceModal, setShowAddEditAllowanceModal] = useState(
    false
  );
  const [activeAllowance, setActiveAllowance] = useState(null);

  const [freezePrevMonth, currentMonth, prevMonth] = activePeriod();

  const queryVariables = useMemo(() => {
    return {
      first: grid.metaData.pagination.pageSize,
      page: grid.metaData.pagination.page,
      filters: grid.metaData.filters,
      sort: grid.metaData.sorting,
    };
  }, [
    grid.metaData.pagination.pageSize,
    grid.metaData.pagination.page,
    grid.metaData.filters,
    grid.metaData.sorting,
  ]);

  const { loading, data, error, refetch } = useQuery(ALLOWANCES_QUERY, {
    variables: queryVariables,
  });

  console.log('ERROR', error);

  const [
    lookupEmployee,
    { data: lookupData, loading: fetching },
  ] = useLazyQuery(EMPLOYEE_LOOKUP_QUERY);

  const { loading: loadingTypes, data: types } = useQuery(
    ALLOWANCE_TYPE_OPTIONS_QUERY
  );

  const [addAllowance, { loading: adding }] = useMutation(
    ADD_ALLOWANCE_MUTATION
  );
  const [updateAllowance, { loading: updating }] = useMutation(
    UPDATE_ALLOWANCE_MUTATION
  );
  const [deleteAllowance, { loading: deleting }] = useMutation(
    DELETE_ALLOWANCE_MUTATION
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  const onEmployeeLookup = (search) => {
    lookupEmployee({
      variables: {
        search,
        status: 'active',
      },
    });
  };

  const onSuccessMutation = (mesageText = 'Details updated successfully!') => {
    refetch(queryVariables);
    message.success(mesageText);
  };

  const columns = [
    {
      title: 'Sl.No',
      dataIndex: 'id',
      key: 'id',
      serverKey: 'ID',
      sorter: true,
      canExclude: false,
      visible: true,
      dataType: 'string',
      className: 'width-100',
    },
    {
      title: 'EMP ID',
      dataIndex: ['employee', 'emp_id'],
      key: 'emp_id',
      serverKey: 'EMP_ID',
      sorter: true,
      canExclude: false,
      visible: true,
      dataType: 'string',
      render: (value, record) => {
        return <ProfileCard employee={record.employee} />;
      },
    },
    {
      title: 'Name',
      dataIndex: ['employee', 'first_name'],
      key: 'first_name',
      serverKey: 'NAME',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
      render: (value, record) => {
        return (
          record && `${record.employee.first_name} ${record.employee.last_name}`
        );
      },
    },
    {
      title: 'Type',
      dataIndex: ['allowance_type', 'name'],
      key: 'allowance_type',
      serverKey: 'TYPE',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'For Month',
      dataIndex: 'for_month',
      key: 'for_month',
      serverKey: 'MONTH',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'date',
      format: 'month',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      serverKey: 'AMOUNT',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
      format: 'currency',
      className: 'cell-currency',
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      serverKey: 'NOTE',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'Created By',
      dataIndex: ['createdBy', 'name'],
      key: 'created_by',
      serverKey: 'USER',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: false,
      dataType: 'string',
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      serverKey: 'CREATED_AT',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: false,
      dataType: 'date',
      format: 'date',
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      serverKey: 'ACTIONS',
      sorter: false,
      canExclude: false,
      visible: true,
      ellipsis: false,
      disableSearch: true,
      className: 'width-75',
      render: (value, record) => {
        const for_month = moment(record.for_month);
        const isEditable = disabledDate(
          for_month,
          freezePrevMonth,
          currentMonth,
          prevMonth
        );
        return (
          <Space>
            {ability.toEditAllowance && (
              <Button
                type="default"
                size="small"
                icon={<EditOutlined />}
                title="Edit"
                onClick={() => {
                  setActiveAllowance(record);
                  setShowAddEditAllowanceModal(true);
                }}
                disabled={isEditable}
              ></Button>
            )}
            {ability.toDeleteAllowance && (
              <Popconfirm
                title="Are you sure to delete this?"
                onConfirm={() => {
                  onDeleteConfirm(record);
                }}
                onCancel={() => {}}
                okText="Delete"
                cancelText="No"
                placement="rightTop"
                disabled={isEditable}
              >
                <Button
                  type="ghost"
                  size="small"
                  icon={<DeleteOutlined />}
                  title="Delete"
                  danger
                  disabled={isEditable}
                ></Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  // const columnsForExport = [...columns];

  useEffect(() => {
    if (grid) {
      console.log('refetch for metaData with///', queryVariables);
      refetch(queryVariables);
    }
  }, [dispatch, refetch, grid, queryVariables]);

  const onChange = (updatedMetaData) => {
    console.log('onChange///', updatedMetaData);
    dispatch(
      updateTableMetaData({
        metaData: {
          ...metaData,
          ...updatedMetaData,
        },
      })
    );
    dispatch(
      updateGridMetaData({
        gridId,
        metaData: updatedMetaData,
      })
    );
  };

  const onAllowanceSubmit = (values, callBack) => {
    console.log('onAllowanceSubmit called with///', values);
    const isUpdate = values.id && values.id !== null;
    const variables = {
      id: isUpdate ? parseInt(values.id, 10) : values.employee.value,
      input: {
        allowance_type_id: parseInt(values.allowance_type_id, 10),
        for_month: values.for_month
          ? values.for_month.startOf('month').format(Config.DATE_FORMAT_SERVER)
          : null,
        amount: values.amount ? values.amount : '',
        note: values.note ? values.note : '',
      },
    };
    const mutationToCall = isUpdate ? updateAllowance : addAllowance;
    mutationToCall({
      variables,
    })
      .then(({ data }) => {
        const allowance = isUpdate ? data.updateAllowance : data.addAllowance;
        console.log('onAllowanceSubmit finished', allowance);
        if (allowance) {
          callBack({ status: true });
          onSuccessMutation();
          setShowAddEditAllowanceModal(false);
        }
        setActiveAllowance(null);
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
        callBack({ status: false });
      });
  };

  const onDeleteConfirm = (allowance) => {
    console.log('onDeleteConfirm called with///', allowance);
    deleteAllowance({
      variables: {
        id: allowance.id,
      },
    })
      .then(({ data: { deleteAllowance } }) => {
        console.log('onDeleteConfirm finished', deleteAllowance);
        if (deleteAllowance.id) {
          onSuccessMutation('Allowance deleted!');
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  const onAddEditModalClose = () => {
    setShowAddEditAllowanceModal(false);
    setActiveAllowance(null);
  };

  console.log('fetching ///2', fetching);

  return (
    <Spin spinning={loading || loadingTypes || adding || updating || deleting}>
      <Card className="employeesCard" style={{ minHeight: '200px' }}>
        {/* {error && <Error message={error.message} />} */}

        {data && (
          <DataGrid
            dataSource={data.allowances.data}
            columns={columns}
            rowSelection={rowSelection}
            filters={grid.metaData.filters}
            visibleColumns={grid.metaData.visibleColumns}
            sorting={grid.metaData.sorting}
            pagination={grid.metaData.pagination}
            onChange={onChange}
            totalRecords={data.allowances.paginatorInfo.total}
            onClearFilter={() => {
              dispatch(resetGridFilters({ gridId }));
            }}
            onAddNewEntry={
              ability.toDeleteAllowance &&
              (() => {
                setShowAddEditAllowanceModal(true);
              })
            }
            addNewEntryBtnLabel="Add Allowance"
          />
        )}
      </Card>
      <Modal
        title={activeAllowance ? 'Edit Allowance' : 'Add Allowance'}
        visible={showAddEditAllowanceModal}
        confirmLoading={false}
        destroyOnClose={true}
        centered
        style={{ maxWidth: '700px' }}
        width="80%"
        footer={null}
        onCancel={onAddEditModalClose}
        maskClosable={false}
      >
        <Spin spinning={adding || updating}>
          <AddAllowanceForm
            allowance={activeAllowance}
            lookupData={
              lookupData && lookupData.employeeLookup
                ? lookupData.employeeLookup.map((i) => {
                    return {
                      label: `${i.first_name} ${i.last_name} (${i.emp_id})`,
                      value: i.id,
                    };
                  })
                : []
            }
            onLookup={onEmployeeLookup}
            types={types ? types.allowanceTypes : []}
            onAllowanceSubmit={onAllowanceSubmit}
            onCancelClick={onAddEditModalClose}
            fetching={fetching}
            ability={ability}
          />
        </Spin>
      </Modal>
    </Spin>
  );
};

export default Allowances;
